import { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, MinusSquare, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { HotelInfo } from '@/types/hotel';
import { buildPromptFromHotelData } from '@/lib/utils/promptUtils';
import { supabase } from '@/integrations/supabase/client';

interface ChatWidgetProps {
  hotelInfo?: HotelInfo;
  factSheetText?: string;
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  secondaryColor?: string;
  isMockup?: boolean;
  lang?: string;
}

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  success?: boolean;
}

const defaultResponses = [
  "I'd be happy to help with that! Our standard check-in time is 3:00 PM and check-out is at 11:00 AM.",
  "Our hotel offers free WiFi throughout the property, a fitness center, pool, and an on-site restaurant.",
  "Yes, we do offer airport shuttle service. It's complimentary for our guests and runs every hour from 6 AM to 11 PM.",
  "Our cancellation policy allows free cancellation up to 24 hours before your scheduled arrival.",
  "For the best rates, I recommend booking directly through our website or calling our reservation team at the front desk.",
];

const ChatWidget = ({ 
  hotelInfo, 
  factSheetText,
  position = 'bottom-right', 
  primaryColor = '#1A365D',
  secondaryColor = '#4299E1',
  isMockup = false,
  lang = 'EN'
}: ChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(() => {
    // Get the container element
    const container = document.querySelector('[data-lobier-chat]');
    // Check if data-open attribute exists and is "true"
    return container?.getAttribute('data-open') === 'true';
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const hotelName = hotelInfo?.name || "Hotel Oceanic";
  
  // Initialize visitor ID
  useEffect(() => {
    const storedVisitorId = localStorage.getItem('lobier_visitor_id');
    if (storedVisitorId) {
      console.log('Found existing visitor ID:', storedVisitorId);
      setVisitorId(storedVisitorId);
    } else {
      const newVisitorId = crypto.randomUUID();
      console.log('Generated new visitor ID:', newVisitorId);
      localStorage.setItem('lobier_visitor_id', newVisitorId);
      setVisitorId(newVisitorId);
    }
  }, []);
  
  // Handle anonymous authentication
  useEffect(() => {
    const checkAndSignIn = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setIsAuthenticating(true);
          // Sign in anonymously
          const { error } = await supabase.auth.signInWithPassword({
            email: `guest-${Date.now()}@lobier.com`,
            password: Math.random().toString(36).slice(-8),
          });
          if (error) {
            console.error('Anonymous sign in error:', error);
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setIsAuthenticating(false);
      }
    };

    checkAndSignIn();
  }, []);
  
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: '1',
        content: `Hello! I'm your AI concierge at ${hotelName}. How can I help you today?`,
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, hotelName]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  const sendMessage = async (userInput: string) => {
    try {
      if (!hotelInfo?.id) {
        throw new Error('Hotel ID is required');
      }

      if (!visitorId) {
        throw new Error('Visitor ID not initialized');
      }

      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      console.log('Sending request with:', {
        hotelId: hotelInfo.id,
        message: userInput,
        sessionId: visitorId,
        lang: lang,
        authToken: session.access_token
      });

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          hotelId: hotelInfo.id,
          message: userInput,
          sessionId: visitorId,
          lang: lang
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Success response:', data);
      console.log('Model used for this response:', data.model || 'Not specified in response');
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      return {
        answer: data.answer,
        success: data.success
      };
      
    } catch (error) {
      console.error('Error in sendMessage:', error);
      if (error instanceof Error) {
        return {
          answer: `I apologize, but I encountered an error: ${error.message}. Please try again later.`,
          success: false
        };
      }
      return {
        answer: 'I apologize, but I\'m having trouble connecting to my knowledge base. Please try again later.',
        success: false
      };
    }
  };
  
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    
    // Add user message
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      content: userMessage,
      sender: 'user',
      timestamp: new Date()
    }]);
    
    try {
      const { answer, success } = await sendMessage(userMessage);
      
      // Add bot response
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        content: answer,
        sender: 'bot',
        timestamp: new Date(),
        success
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I encountered an error. Please try again later.',
        sender: 'bot',
        timestamp: new Date(),
        success: false
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div 
      className={cn(
        "fixed z-50 transition-all duration-300",
        position === 'bottom-right' ? 'bottom-4 right-4' : 'bottom-4 left-4'
      )}
      style={{ 
        ...(isMockup ? { position: 'relative', bottom: 'auto', right: 'auto' } : {})
      }}
    >
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          style={{ backgroundColor: primaryColor }}
        >
          <MessageSquare className="w-6 h-6 text-white" />
        </button>
      )}
      
      {isOpen && (
        <div 
          className="bg-white rounded-lg shadow-xl flex flex-col w-[350px] h-[500px] overflow-hidden"
          style={{ 
            ...(isMockup ? { width: '100%', height: '500px' } : {})
          }}
        >
          <div 
            className="p-4 flex justify-between items-center"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5 text-white" />
              <h3 className="font-medium text-white">Chat with {hotelName}</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-white hover:text-gray-200 transition-colors"
              >
                <MinusSquare className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={cn(
                  "chat-bubble",
                  message.sender === 'user' ? "user-bubble" : "bot-bubble"
                )}
              >
                {message.content}
              </div>
            ))}
            {isLoading && (
              <div className="bot-bubble flex items-center space-x-2">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Typing...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <form onSubmit={handleSendMessage} className="p-4 border-t">
            <div className="flex space-x-2">
              <Input
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="flex-1"
                disabled={isLoading}
              />
              <Button 
                type="submit" 
                size="icon" 
                disabled={isLoading}
                style={{ backgroundColor: secondaryColor }}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
